import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnDestroy, SimpleChanges, HostBinding, OnChanges, ChangeDetectionStrategy, ChangeDetectorRef, Injectable, input, output, computed, inject, effect, EffectRef, signal } from '@angular/core';
import moment, { Moment, isMoment } from 'moment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CultureService } from '../../services/culture.service';
import { DateAdapter, ErrorStateMatcher, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule} from '@angular/material/core';
import { BehaviorSubject, Subject, Subscription, takeUntil } from 'rxjs';
import { FormControl, FormGroup, FormGroupDirective, FormsModule, NgForm, ReactiveFormsModule, ValidationErrors, Validator, Validators } from '@angular/forms';
import { SE_UI_DATE_TIME_FORMATS } from '../se-ui-date-formats';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter, provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_TIMEPICKER_CONFIG, MatTimepicker, MatTimepickerModule, MatTimepickerSelected } from '@angular/material/timepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { isNil, update } from 'lodash-es';
import { setClassPanelAgGrid } from '../se-helper-pickers';
import { NgClass } from '@angular/common';

export class DateTimePickerStateMatcher implements ErrorStateMatcher {
    error$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    forceError = false;
    required = false;

    isErrorState(control: FormControl | null, _: FormGroupDirective | NgForm | null): boolean {
        let isOnError = this.forceError || !!(control && control.invalid && this.required);
        if (isOnError != this.error$.value) {
            this.error$.next(isOnError);
        }

        return isOnError;
    }
}

export interface TimePart {
    hour: number;
    minute: number;
    second: number;
}

@Component({
    selector: 'se-datetime-picker',
    templateUrl: './se-datetime-picker.component.html',
    styleUrls: ['./se-datetime-picker.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: SE_UI_DATE_TIME_FORMATS },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE],
        }
    ],
    imports: [
        MatTimepickerModule,
        FormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatDatepickerModule,
        MatInputModule,
        ReactiveFormsModule,
        TranslateModule,
        NgClass
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SeDatetimePickerComponent implements OnDestroy, OnInit {
    @HostBinding('style.display')
    display = 'flex';

    @Output()
    dateTimePickerKeyDown = new EventEmitter<{ ev: KeyboardEvent; picker: 'date' | 'hour' | 'minute' }>();

    @ViewChild('seTimePickerInput', { static: false, read: ElementRef }) timePickerInput: ElementRef;

    @ViewChild('seTimePickerInput_Time', { static: false, read: ElementRef }) timePicker: ElementRef;

    @ViewChild('dateInput', { static: false, read: ElementRef }) dateInput: ElementRef;

    // errors
    dateTimePickerMatcher: DateTimePickerStateMatcher = new DateTimePickerStateMatcher();
    isOnError = signal<boolean>(false);

    //format
    timeFormat: string = '';
    dateFormat: string = '';
    timeDateFormat: string = '';

    private isOnErrorSubscriber!: Subscription;
    private time: TimePart | null = null;
    private _onDestroy = new Subject<void>();

    //inject
    private readonly translateService = inject(TranslateService);
    private readonly culture = inject(CultureService);

    //input
    appearance = input<string>('outline');
    clockAmPm = input<number>(0);
    customPanelClass = input<string>('');
    dateTime = input<moment.Moment | null>();
    disabled = input<boolean>(false);
    gridClass = input<string>('');
    highlightBackgroundEnabled = input<boolean>(false);
    hourPlaceholder = input<string>('hh'); 
    isWithLabel = input<boolean>(true);
    label = input<string>(this.translateService.instant('Date') + ' & ' + this.translateService.instant('Time'));
    minutePlaceholder = input<string>('MM'); 
    required = input<boolean>(false);
    setError = input<boolean>(false);
    showDateFormatOnHint = input<boolean>(false);
    showIcon = input<boolean>(true);
    showResetButton = input<boolean>(true);

    //output
    dateTimeChange = output<moment.Moment | null>();
    errorStateChange = output<boolean>();

    dateControl: FormControl<moment.Moment>;
    private dateTimeEffect: EffectRef;

    constructor(private changeDetectionRef: ChangeDetectorRef) {
        this.dateTimeEffect = effect(() => {
            const date = this.dateTime();
            if (date == null || !date.isValid()) {
                this.dateControl.patchValue(null, { emitEvent: false });
                if (this.timePickerInput) {
                    this.timePickerInput.nativeElement.value = '';
                }
            } else {
                if (this.dateControl.value !== date && date.isValid()) {
                    this.dateControl.patchValue(date, { emitEvent: false });
                }

                if (this.disabled() !== this.dateControl.disabled) {
                    this.disabled() ? this.dateControl.disable({ emitEvent: false }) : this.dateControl.enable({ emitEvent: false });
                }
            }
           
            let validators = this.required() ? [Validators.required] : [];
            this.dateControl.setValidators(validators);
            this.dateControl.updateValueAndValidity({ emitEvent: false });
        })
    }

    ngOnDestroy() {
        if (this.isOnErrorSubscriber) {
            this.isOnErrorSubscriber.unsubscribe();
        }
        this._onDestroy.unsubscribe();
        this.dateTimeEffect.destroy();
    }

    ngOnInit() {
        this.timeFormat = this.culture.getShortTimePattern();
        this.dateFormat = this.culture.getShortDatePattern();
        this.timeDateFormat = this.culture.getShortDateTimePattern();
        if (this.culture.getCulture() == 'en-US' || (this.clockAmPm() && this.clockAmPm() === 12)) {
            this.setFormat();
        }

        this.initFormControl();

        this.isOnErrorSubscriber = this.dateTimePickerMatcher.error$.subscribe((value) => {
            if (this.isOnError() != value) {
                this.isOnError.set(value);
                this.errorStateChange.emit(value);
            }
        });

        this.dateControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe({
            next: (value) => {
                this.dateTimeChange.emit(moment(value));
                if (value && value.isValid()) {
                    this.dateTimePickerMatcher.forceError = false;

                }
            }
        })
    }

    private setFormat() {
        //format 12 di default è 24
        SE_UI_DATE_TIME_FORMATS.display.timeOptionLabel = 'h:mm A';
        this.timeFormat = 'h:mm A';
    }

    private initFormControl() {
        const date = this.dateTime() ?? null;
        if (!this.required()) {
            this.dateControl = new FormControl<moment.Moment | null>({ value: date, disabled: this.disabled() }, { updateOn: 'blur' });
        } else {
            this.dateControl = new FormControl<moment.Moment | null>({ value: date, disabled: this.disabled() }, { validators: Validators.required, updateOn: 'blur' });
        }
        this.dateTimeChange.emit(date);
    }

    private setNow() {
        this.dateControl.patchValue(moment(Date.now()));
    }

    getErrorMessage(): string {
        return this.required() && this.isOnError() ? this.translateService.instant('MandatoryData') : this.translateService.instant('Error');
    }

    onDateTimeKeydown(e: KeyboardEvent) {
        this.dateTimePickerKeyDown.emit({
            ev: e,
            picker: 'date',
        });

        switch (e.key) {
            case ' ':
                e.preventDefault();
                this.setNow();
                break;
            case 'Delete':
                e.preventDefault();
                this.onResetDateTime();
                break;
            default:
                break;
        }
    }

    onResetDateTime() {
        this.dateControl.patchValue(null);
        this.time = null;
        this.dateTimePickerMatcher.error$.next(false);
    }

    onTimeChanging(eventTime: MatTimepickerSelected<moment.Moment>) {
        if (eventTime) {
            const time: TimePart = {
                hour: eventTime.value.hours(),
                minute: eventTime.value.minutes(),
                second: eventTime.value.seconds(),
            }
            let date = this.dateControl.value?.toDate();
            if (date) {
                date.setHours(time.hour, time.minute, time.second);
                this.dateControl.patchValue(moment(date));
                this.changeDetectionRef.detectChanges();
            }
        }
    }

    onDateChanging(eventDate: MatDatepickerInputEvent<moment.Moment, any>) {
        if (eventDate) {
            const date = isMoment(eventDate.value) ? eventDate.value : null;
            this.dateControl.patchValue(date);
            if (!date && this.required()) {
                this.dateTimePickerMatcher.required = true;
                this.dateTimePickerMatcher.error$.next(true);
            }
        }
    }

    onOpenTime() {
        if (this.customPanelClass()) {
            //al momento il timepicker non ha un panel class
            //se non mettiamo la classe di ag grid in griglia non si può selezionare
            setClassPanelAgGrid(this.customPanelClass());
        }
    }

    onCloseTime() {
        this.dateTimeChange.emit(this.dateControl.value);
        this.changeDetectionRef.detectChanges();
    }

    // Funzioni di manipolazione dall'esterno

    focus() {
        this.dateInput.nativeElement.focus();
    }

    focusHour() {
        this.dateInput.nativeElement.focus();
    }

    select() {
        this.dateInput.nativeElement.select();
    }

    getDateFormat() {
        return this.dateFormat;
    }

    onClick(ev:any) {
        ev.preventDefault();
    }

}
